/** Inline action cards rendered inside assistant chat messages (chats SPA variant). The SPA embeds inside the FEDERA
 *  shell same-origin, so the app-internal routes the shared cards call ARE reachable: InlineFunctionCard fetches by
 *  executionId and runs approve/decline; InlinePropertyCard hydrates from the public property-by-id endpoint. */

import { useEffect, useState } from 'react';
import { useInViewOnce } from '@federa/components/hooks/useInViewOnce';
import { spacing } from '@federa/components/css-refs.spacing';
import { colors } from '@federa/components/css-refs.colors';
import { TEXT_SM_SEMIBOLD_PRIMARY } from '@federa/components/common/layout-styles';
import { borderRadius } from '@federa/components/css-refs.effects';
import { ARIA_ROLES } from '@federa/components/common/aria-tokens';
import { FunctionCard } from '@federa/components/domain-cards/FunctionCard';
import { PropertyCard } from '@federa/components/domain-cards/PropertyCard';
import { isNonEmptyArray } from '@federa/const/array-guards';
import { SENTINEL_PROPERTY_IMAGE } from '@federa/design-system/assets/images';
import { buildPropertyPath } from '@federa/const/routes';
import { buildPublicPropertyDetailsPath } from '@federa/const/routes-api';
import { getFunctionDetails } from '@federa/components/lib/api/functions.execution';
import { transformPropertyToCardData } from '@federa/components/property/property-card';
import { parseProperty } from '@federa/components/lib/websocket/parse';
import { coerceRecord } from '@federa/utils/data/coerce-record';
import { safeJsonParse } from '@federa/utils/json/safe-json';
import { secureLogger } from '@federa/utils/deps/registry';
import { LOG_EVENTS } from '@federa/const/logging';
import { buildAcceptJsonGetInit } from '@federa/const/http-request-init';
import { STORAGE_GATEWAY } from '@federa/const/urls-connect';
import { isProductionHostname } from '@federa/utils/config/env-config';
import { isBrowser } from '@federa/utils/browser/detection';
import { BORDER_MUTED, FLEX_COLUMN_GAP1_STYLE, MUTED_LABEL_STYLE } from '@federa/components/common/style-primitives';
import type { CSSProperties, ReactNode } from 'react';
import type { PropertyCardProps } from '@federa/types/ui-property-card';

const TITLE_PROPERTY_MENTIONED = 'Property mentioned';

const TITLE_ACTION_PENDING = 'Action pending';

const SHELL_STYLE: CSSProperties = {
	marginTop: spacing[3],
	...FLEX_COLUMN_GAP1_STYLE,
};

const CARD_STYLE: CSSProperties = {
	...FLEX_COLUMN_GAP1_STYLE,
	padding: spacing[3],
	border: BORDER_MUTED,
	borderRadius: borderRadius.lg,
	backgroundColor: colors.background.coolNeutral,
};

/** Compact fallback used while a card hydrates or when its fetch misses. */
function InfoCard({ title, children }: { title: string; children: ReactNode }) {
	return (
		<div style={SHELL_STYLE}>
			<div style={CARD_STYLE} role={ARIA_ROLES.status}>
				<span style={TEXT_SM_SEMIBOLD_PRIMARY}>{title}</span>
				<span style={MUTED_LABEL_STYLE}>{children}</span>
			</div>
		</div>
	);
}

function normalizePropertyPayload(payload: unknown): unknown {
	if (!payload) return null;
	if (isNonEmptyArray(payload)) return payload[0];
	if (typeof payload === 'string') return safeJsonParse(payload, null);
	const record = coerceRecord(payload);
	if (!record) return null;
	if (record.property_details) return record;
	if (record.property) return record.property;
	if (record.data) return normalizePropertyPayload(record.data);
	if (record.result) return normalizePropertyPayload(record.result);
	if (record.results) return normalizePropertyPayload(record.results);
	if (record.body) return normalizePropertyPayload(record.body);
	return record;
}

/** Resolve the storage-gateway HTTP base for the current host (mirrors resolveWsBase). The public property endpoint
 *  lives on the storage-gateway worker, NOT the FEDERA app -- a relative URL 404'd and degraded every property card. */
function resolveStorageGatewayHttpBase(): string {
	if (!isBrowser()) return STORAGE_GATEWAY.DEV_URL;
	return isProductionHostname(window.location.hostname) ? STORAGE_GATEWAY.PROD_URL : STORAGE_GATEWAY.DEV_URL;
}

/** Fetch a property by id from the storage-gateway public endpoint. Returns card-ready data, or null on any miss (404 / parse failure), which the caller renders as a fallback. */
async function fetchPropertyCardData(propertyId: string): Promise<PropertyCardProps['property'] | null> {
	if (!propertyId) return null;
	try {
		const base = resolveStorageGatewayHttpBase();
		// SSOT path, not a hand-rebuilt one: `buildPublicPropertyDetailsPath` fills the single capture in
		// `GATEWAY_ROUTE_PATHS.PUBLIC_PROPERTY_DETAILS`, the same const the gateway REGISTERS the route from, and encodes
		// the id on the way. The literal this replaced was a second spelling of that route with nothing tying the two
		// together, so a segment rename would have 404'd this card silently (it renders the fallback on any miss).
		const response = await fetch(`${base}${buildPublicPropertyDetailsPath(propertyId)}`, buildAcceptJsonGetInit());
		if (!response.ok) return null;
		const payload: unknown = await response.json().catch(() => null);
		const property = parseProperty(normalizePropertyPayload(payload));
		if (!property) return null;
		return transformPropertyToCardData(property);
	} catch (error) {
		secureLogger.error(LOG_EVENTS.INLINE_PROPERTY_CARD_PROPERTY_FETCH_FAILED, { error, propertyId });
		return null;
	}
}

/** Shared in-view fetch gate for BOTH inline cards: fetch only once the card has been LOOKED AT (opening a conversation
 *  used to issue one request per card in the whole history, including cards far above the scroll position the user never
 *  reaches), and drop the settle when the effect re-ran or the card unmounted first. `fetchValue` must be referentially
 *  stable (a module-scope function), or the effect re-fires every render. */
function useInViewGatedFetch<T>(
	id: string,
	fetchValue: (id: string) => Promise<T | null>,
	logEvent: string,
): { ref: ReturnType<typeof useInViewOnce>['ref']; loading: boolean; value: T | null } {
	const [state, setState] = useState<{ loading: boolean; value: T | null }>({ loading: true, value: null });
	const { ref, inView } = useInViewOnce();

	useEffect(() => {
		if (!inView) return undefined;
		let cancelled = false;
		fetchValue(id)
			.then((value) => {
				if (cancelled) return;
				setState({ loading: false, value });
			})
			.catch((error: unknown) => {
				if (cancelled) return;
				secureLogger.error(logEvent, { error, id });
				setState({ loading: false, value: null });
			});
		return () => {
			cancelled = true;
		};
	}, [id, inView, fetchValue, logEvent]);

	return { ref, loading: state.loading, value: state.value };
}

interface InlineFunctionCardProps {
	executionId: string;
}

interface FunctionCardDetails {
	functionTitle?: string;
	argumentPayload?: string;
	status: string;
}

/** The loaded function-card projection, or null when the execution has no reviewable status. Module-scope so the hook's fetch dep stays stable. */
async function fetchFunctionCardDetails(executionId: string): Promise<FunctionCardDetails | null> {
	const result = await getFunctionDetails({ executionId });
	if (!result.success || !result.functionStatus) return null;
	return { functionTitle: result.functionTitle, argumentPayload: result.arguments, status: result.functionStatus };
}

/** Live approve/decline function card. Key-remount wrapper: a changed executionId resets loading via initial state, not an effect. */
export function InlineFunctionCard({ executionId }: InlineFunctionCardProps) {
	return <InlineFunctionCardInner key={executionId} executionId={executionId} />;
}

function InlineFunctionCardInner({ executionId }: InlineFunctionCardProps) {
	const {
		ref,
		loading,
		value: details,
	} = useInViewGatedFetch(executionId, fetchFunctionCardDetails, 'InlineFunctionCard: details fetch failed');

	// The ref rides an ALWAYS-rendered wrapper: the observer needs a mounted element while the card is still a
	// placeholder, which is exactly the state it is in before it scrolls into view.
	if (loading) {
		return (
			<div ref={ref}>
				<InfoCard title={TITLE_ACTION_PENDING}>Loading action details...</InfoCard>
			</div>
		);
	}
	if (!details) {
		return (
			<div ref={ref}>
				<InfoCard title={TITLE_ACTION_PENDING}>Open the FEDERA app to review and approve this action (ref {executionId}).</InfoCard>
			</div>
		);
	}

	return (
		<div ref={ref} style={SHELL_STYLE}>
			<FunctionCard
				functionTitle={details.functionTitle}
				arguments={details.argumentPayload}
				executionId={executionId}
				status={details.status}
				// Stay in the assistant after resolving (empty path suppresses the shared card's post-accept navigation to explore).
				explorePath=""
			/>
		</div>
	);
}

interface InlinePropertyCardProps {
	propertyId: string;
}

/** Rich inline property card, hydrated from a same-origin property fetch. Key-remount wrapper (see InlineFunctionCard): a changed propertyId mounts a fresh inner card so loading resets via initial state. */
export function InlinePropertyCard({ propertyId }: InlinePropertyCardProps) {
	return <InlinePropertyCardInner key={propertyId} propertyId={propertyId} />;
}

function InlinePropertyCardInner({ propertyId }: InlinePropertyCardProps) {
	// Same gate as the function card; fetchPropertyCardData resolves null on its own catch, so the hook's catch arm only
	// terminates the chain for the promise-consumption contract.
	const {
		ref,
		loading,
		value: cardData,
	} = useInViewGatedFetch(propertyId, fetchPropertyCardData, 'InlinePropertyCard: unexpected fetch rejection');

	if (loading) {
		return (
			<div ref={ref}>
				<InfoCard title={TITLE_PROPERTY_MENTIONED}>Loading property...</InfoCard>
			</div>
		);
	}
	if (!cardData) {
		return (
			<div ref={ref}>
				<InfoCard title={TITLE_PROPERTY_MENTIONED}>View property {propertyId} in the FEDERA app.</InfoCard>
			</div>
		);
	}

	// Clickable, same as the feed tile: the chats SPA is same-origin with the FEDERA shell, so the relative property path is a live route. onClick drives the navigation (PropertyCard preventDefaults the anchor and calls it); href preserves middle-click / open-in-new-tab. sentinelImageSrc supplies the brand "Photo coming soon" placeholder shown on the feed when a property has no image.
	const propertyPath = buildPropertyPath(cardData.id || propertyId);
	return (
		<div style={SHELL_STYLE}>
			<PropertyCard property={cardData} sentinelImageSrc={SENTINEL_PROPERTY_IMAGE} onClick={openPropertyPage} href={propertyPath} />
		</div>
	);
}

/** Navigate the FEDERA shell to a property detail page (same-origin route). */
function openPropertyPage(id: string): void {
	if (isBrowser()) window.location.assign(buildPropertyPath(id));
}
